// FIN-7157: Einstellungen → Struktur (company structure of a company admin)

export type StatisticsPeriod = 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_MONTH' | 'LAST_MONTH';
export type CompanyStructureAdvisorSortField = 'name' | 'package' | 'lastLogin';

export interface CompanyStructureAdvisorPageRequest {
  readonly page: number;
  readonly pageSize: number;
  readonly sortField: CompanyStructureAdvisorSortField;
  readonly sortDirection: 'asc' | 'desc';
}

export interface CompanyStructureAdvisorPageModel {
  readonly data: readonly CompanyStructureAdvisorModel[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

export interface CompanyStructureNodeModel {
  readonly id: number;
  readonly name: string;
  readonly advisorCount: number;
  readonly children: readonly CompanyStructureNodeModel[];
}

export interface CompanyStructureModel {
  readonly id: number;
  readonly name: string;
  readonly logoUrl: string | null;
  readonly contactPerson: string | null;
  readonly companyCount: number;
  readonly advisorCount: number;
  readonly children: readonly CompanyStructureNodeModel[];
  /** Only admins of the root company may switch features (Einstellungen → Struktur → Funktionen). */
  readonly canManageFeatures: boolean;
}

export interface CompanyStructureAdvisorModel {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly profilePictureUrl: string | null;
  readonly package: string | null;
  readonly packageName: string | null;
  readonly registeredClientCount: number;
  readonly onboardingClientCount: number;
  readonly contractCount: number;
  readonly lastLogin: string | null;
  readonly absentUntil: string | null; // 'YYYY-MM-DD', set while the advisor is absent
}

export interface CompanyStructureCompanyModel {
  readonly id: number;
  readonly name: string;
  readonly advisorCount: number;
  readonly clientCount: number;
  readonly contractCount: number;
  readonly advisors: CompanyStructureAdvisorPageModel;
}

export interface CompanyStructureAdvisorStatisticsModel {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly profilePictureUrl: string | null;
  readonly package: string | null;
  readonly packageName: string | null;
  readonly companyId: number;
  readonly companyName: string;
  readonly period: StatisticsPeriod;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly clientCount: number;
  readonly registeredClientCount: number;
  readonly onboardingClientCount: number;
  readonly contractCount: number;
  readonly contractDensity: number | null;
  readonly lastLogin: string | null;
  readonly periodContractCount: number;
  readonly periodContractVolume: number;
}

// Feature switches of the structure (Struktur → Funktionen). Only newly launched features, never add-ons.

export type StructureFeature = 'DEPOTS' | 'PROVISION_VIEW';

export interface StructureFeatureCount {
  readonly enabled: number;
  readonly total: number;
}

export interface CompanyStructureFeaturesAdvisorModel {
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly features: Readonly<Record<StructureFeature, boolean>>;
}

export interface CompanyStructureFeaturesCompanyModel {
  readonly id: number;
  readonly name: string;
  readonly features: Readonly<Record<StructureFeature, StructureFeatureCount>>;
  readonly advisors: readonly CompanyStructureFeaturesAdvisorModel[];
  readonly children: readonly CompanyStructureFeaturesCompanyModel[];
}

export interface CompanyStructureFeaturesModel {
  readonly features: readonly StructureFeature[];
  readonly root: CompanyStructureFeaturesCompanyModel;
}

/** Exactly one of companyId / advisorId is set; a company change applies to everything below it. */
export interface CompanyStructureFeatureChange {
  readonly feature: StructureFeature;
  readonly companyId?: number;
  readonly advisorId?: string;
  readonly enabled: boolean;
}
